/**
 * PillarSourcesPanel — expandable drill-down showing the source answers
 * that fed into a single pillar's score: hard-filter answers + onboarding
 * (Abby) answers per user.
 *
 * Backend contract:
 *   GET /v1/admin/matches/:matchId/pillar-sources?pillar_title=<URL-enc>
 *
 * Spec: docs/superpowers/specs/2026-05-28-admin-matches-summaries-design.md §7.2 / §6.4
 */
import { useState } from 'react';
import { useApiUrl } from '@refinedev/core';
import { adminFetch, AdminApiError } from '@/providers/admin-api';

interface HardFilterAnswer {
  question_key: string;
  question_text: string;
  category: string;
  selected_options: string[] | null;
  numeric_value: number | null;
  answered_at: string | null;
}

interface OnboardingAnswer {
  question_id: string;
  question_text: string;
  category: string | null;
  text_value: string | null;
  numeric_value: number | null;
  selected_options: string[] | null;
  answered_at: string | null;
}

interface SourcesPayload {
  pillar_title: string;
  user1_id: string;
  user2_id: string;
  user1_hard_filter_answers: HardFilterAnswer[];
  user2_hard_filter_answers: HardFilterAnswer[];
  user1_onboarding_answers: OnboardingAnswer[];
  user2_onboarding_answers: OnboardingAnswer[];
}

interface Props {
  matchId: string;
  pillarTitle: string;
  user1Label?: string;
  user2Label?: string;
}

export function PillarSourcesPanel({
  matchId,
  pillarTitle,
  user1Label = 'User A',
  user2Label = 'User B',
}: Props) {
  const apiUrl = useApiUrl();
  const [data, setData] = useState<SourcesPayload | null>(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    setErr(null);
    try {
      const r = await adminFetch(
        `${apiUrl}/v1/admin/matches/${matchId}/pillar-sources?pillar_title=${encodeURIComponent(pillarTitle)}`,
      );
      const json = (await r.json()) as SourcesPayload;
      setData(json);
    } catch (e: unknown) {
      setErr(e instanceof AdminApiError ? e.message : 'Failed to load source answers');
    } finally {
      setLoading(false);
    }
  };

  if (!data) {
    return (
      <div style={{ marginTop: 8 }}>
        <button
          type="button"
          onClick={load}
          disabled={loading}
          className={inlineButtonClass}
        >
          {loading ? 'Loading…' : 'View source answers'}
        </button>
        {err && <span className="ml-2 text-xs text-destructive">{err}</span>}
      </div>
    );
  }

  return (
    <div className="mt-2 rounded-md border bg-muted/50 p-2.5 text-xs">
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <SourceColumn
          title={`${user1Label} hard-filter (${data.user1_hard_filter_answers.length})`}
          rows={data.user1_hard_filter_answers.map(r => ({
            label: r.question_text,
            value: formatHfValue(r),
          }))}
        />
        <SourceColumn
          title={`${user2Label} hard-filter (${data.user2_hard_filter_answers.length})`}
          rows={data.user2_hard_filter_answers.map(r => ({
            label: r.question_text,
            value: formatHfValue(r),
          }))}
        />
        <SourceColumn
          title={`${user1Label} onboarding (${data.user1_onboarding_answers.length})`}
          rows={data.user1_onboarding_answers.map(r => ({
            label: r.question_text,
            value: formatObValue(r),
          }))}
        />
        <SourceColumn
          title={`${user2Label} onboarding (${data.user2_onboarding_answers.length})`}
          rows={data.user2_onboarding_answers.map(r => ({
            label: r.question_text,
            value: formatObValue(r),
          }))}
        />
      </div>
    </div>
  );
}

function SourceColumn({
  title,
  rows,
}: {
  title: string;
  rows: { label: string; value: string }[];
}) {
  return (
    <details open>
      <summary style={{ cursor: 'pointer', fontWeight: 600 }}>{title}</summary>
      {rows.length === 0 ? (
        <p className="my-1 italic text-muted-foreground">
          No answers recorded for this pillar.
        </p>
      ) : (
        <ul style={{ paddingLeft: 18, margin: '4px 0' }}>
          {rows.map((r, i) => (
            <li key={i} style={{ marginBottom: 3 }}>
              <strong>{r.label}:</strong> {r.value}
            </li>
          ))}
        </ul>
      )}
    </details>
  );
}

function formatHfValue(a: HardFilterAnswer): string {
  if (a.selected_options && a.selected_options.length) return a.selected_options.join(', ');
  if (a.numeric_value !== null && a.numeric_value !== undefined) return String(a.numeric_value);
  return '—';
}

function formatObValue(a: OnboardingAnswer): string {
  if (a.text_value) return a.text_value;
  if (a.selected_options && a.selected_options.length) return a.selected_options.join(', ');
  if (a.numeric_value !== null && a.numeric_value !== undefined) return String(a.numeric_value);
  return '—';
}

const inlineButtonClass =
  'cursor-pointer rounded border bg-background px-2.5 py-1 text-xs hover:bg-muted';
